import type { CSSProperties } from "react";

export type LoadingWeatherVariant =
  "clear" | "rain" | "snow" | "heat" | "cold" | "night" | "neutral";

export interface LoadingEnvironmentContext {
  variant: LoadingWeatherVariant;
  locationLabel?: string;
  phrase?: string;
}

interface WindowLoadingContext {
  weather?: LoadingWeatherVariant;
  locationLabel?: string;
  phrase?: string;
}

declare global {
  interface Window {
    __FINTHEON_LOADING_CONTEXT__?: WindowLoadingContext;
  }
}

const TIMEZONE_LABELS: Record<string, string> = {
  "America/New_York": "East desk",
  "America/Chicago": "Central desk",
  "America/Denver": "Mountain desk",
  "America/Los_Angeles": "Pacific desk",
};

const VARIANT_PHRASES: Record<LoadingWeatherVariant, string> = {
  clear: "Clear tape forming",
  rain: "Rain scan online",
  snow: "Cold tape forming",
  heat: "Heat map warming",
  cold: "Cold open watch",
  night: "Night desk waking",
  neutral: "Restoring session",
};

export function readLoadingEnvironment(): LoadingEnvironmentContext {
  if (typeof window === "undefined") return { variant: "neutral" };

  const provided = window.__FINTHEON_LOADING_CONTEXT__;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hour = new Date().getHours();
  const fallbackVariant = hour < 6 || hour >= 20 ? "night" : "neutral";
  const variant = provided?.weather ?? fallbackVariant;

  return {
    variant,
    locationLabel: provided?.locationLabel ?? TIMEZONE_LABELS[timeZone],
    phrase: provided?.phrase ?? VARIANT_PHRASES[variant],
  };
}

export function loadingEnvironmentStyle(
  context?: LoadingEnvironmentContext,
): CSSProperties {
  const variant = context?.variant ?? "neutral";
  return {
    "--fintheon-loading-overlay": overlayForVariant(variant),
  } as CSSProperties;
}

export function loadingEnvironmentPhrase(
  context?: LoadingEnvironmentContext,
): string | undefined {
  if (!context?.phrase) return undefined;
  if (!context.locationLabel) return context.phrase;
  return `${context.locationLabel} / ${context.phrase}`;
}

function overlayForVariant(variant: LoadingWeatherVariant): string {
  const overlays: Record<LoadingWeatherVariant, string> = {
    clear:
      "radial-gradient(circle at 18% 20%, rgba(199,159,74,.16), transparent 34%)",
    rain: "linear-gradient(115deg, rgba(96,165,250,.12), transparent 38%)",
    snow: "radial-gradient(circle at 72% 18%, rgba(240,234,214,.16), transparent 30%)",
    heat: "radial-gradient(circle at 72% 72%, rgba(248,113,113,.14), transparent 34%)",
    cold: "linear-gradient(145deg, rgba(125,211,252,.12), transparent 40%)",
    night:
      "radial-gradient(circle at 50% 8%, rgba(199,159,74,.10), transparent 36%)",
    neutral: "linear-gradient(135deg, transparent, rgba(199,159,74,.06))",
  };
  return overlays[variant];
}
